
import { Suspense, lazy, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { fadeInVariants, slideInVariants } from "@/lib/animations";
import NavbarSection from "@/components/sections/NavbarSection";
import HeroSection from "@/components/sections/HeroSection";
import DemoSection from "@/components/sections/DemoSection";
import BenefitsSection from "@/components/sections/BenefitsSection";
// import TrustSection from "@/components/sections/TrustSection"; // Desconectado do build
import BackgroundElements from "@/components/index-page/BackgroundElements";
import IntroCards from "@/components/index-page/IntroCards";
import OptimizedSectionLoader from "@/components/index-page/OptimizedSectionLoader";
import PageSEO from "@/components/seo/PageSEO";
import LocalBusinessStructuredData from "@/components/seo/LocalBusinessStructuredData";
import { trackEvent } from "@/lib/analytics";
import { useAnimationSimplifier } from "@/hooks/use-animation-simplifier";
import { useOptimizedViewport } from "@/hooks/useOptimizedViewport";

// Lazy load sections below the fold for better performance
const LazyEmotionalTrackingSection = lazy(() => import("@/components/sections/EmotionalTrackingSection"));
const LazyWorkPerformanceSection = lazy(() => import("@/components/sections/WorkPerformanceSection"));
const LazyFeaturesSection = lazy(() => import("@/components/sections/FeaturesSection"));
const LazyAdaptiveSection = lazy(() => import("@/components/sections/AdaptiveSection"));
const LazyTestimonialsSection = lazy(() => import("@/components/sections/TestimonialsSection"));
const LazyFaqSection = lazy(() => import("@/components/sections/FaqSection"));
const LazyCtaSection = lazy(() => import("@/components/sections/CtaSection"));
const LazyFooterSection = lazy(() => import("@/components/sections/FooterSection"));

// Premium sections - removed LazyPremiumBenefits
const LazyPremiumDemo = lazy(() => import("@/components/sections/premium/PremiumDemo"));

// New sections
const LazyCalmBreathingSection = lazy(() => import("@/components/sections/CalmBreathingSection"));
const LazyMindBodyConnectionSection = lazy(() => import("@/components/sections/MindBodyConnectionSection"));
const LazyTransformationStepsSection = lazy(() => import("@/components/sections/TransformationStepsSection"));

const Index = () => {
  const [initialContentLoaded, setInitialContentLoaded] = useState(false);
  const { shouldSimplifyAnimations } = useAnimationSimplifier();
  const { isMobile } = useOptimizedViewport();
  
  useEffect(() => {
    console.log("Index page loaded");
    
    // Use requestIdleCallback for better performance
    const scheduleContentLoad = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => {
          setInitialContentLoaded(true);
          console.log("Initial content loaded");
        });
      } else {
        setTimeout(() => {
          setInitialContentLoaded(true);
          console.log("Initial content loaded (fallback)");
        }, 500);
      }
    };

    scheduleContentLoad();
    trackEvent('homepage_view');
  }, []);
  
  // Enhanced website structured data for better SEO
  const websiteData = {
    name: "metrocalma",
    url: "https://metrocalma.org",
    description: "Plataforma premium de bem-estar mental e autoconhecimento com ferramentas personalizadas baseadas em ciência",
    inLanguage: "pt-BR",
    publisher: {
      "@type": "Organization",
      "name": "metrocalma",
      "logo": {
        "@type": "ImageObject",
        "url": "https://metrocalma.org/lovable-uploads/3c6e3c80-fdf0-41d2-8c65-22c6d6b09d46.png"
      }
    },
    potentialAction: {
      "@type": "SearchAction",
      target: "https://metrocalma.org/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    offers: {
      "@type": "Offer",
      "description": "Planos premium de bem-estar mental",
      "priceCurrency": "BRL",
      "availability": "https://schema.org/InStock"
    }
  };

  // Enhanced breadcrumb data for SEO
  const breadcrumbs = [
    {
      position: 1,
      name: "Início",
      item: "https://metrocalma.org"
    }
  ];

  const animationProps = shouldSimplifyAnimations ? {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.2 }
  } : {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.3 }
  };
  
  return (
    <motion.div 
      className="min-h-screen flex flex-col relative overflow-hidden"
      {...animationProps}
    >
      <PageSEO 
        title="metrocalma - Desperte sua melhor versão com carinho e inteligência"
        description="Uma jornada única de autoconhecimento e bem-estar emocional. Ferramentas personalizadas, baseadas em ciência, para cuidar da sua saúde mental com carinho e sofisticação."
        keywords="saúde mental premium, bem-estar emocional, autoconhecimento, terapia personalizada, meditação, mindfulness, crescimento pessoal, psicologia, teste personalidade, Young schemas, big five"
        canonicalUrl="https://metrocalma.org"
        ogImage="https://metrocalma.org/lovable-uploads/3c6e3c80-fdf0-41d2-8c65-22c6d6b09d46.png"
        pageName="Home"
        pageType="WebSite"
        structuredData={websiteData}
        breadcrumbs={breadcrumbs}
        dateModified="2025-06-01"
      />
      
      {/* Add location-specific structured data for Local SEO */}
      {initialContentLoaded && (
        <LocalBusinessStructuredData 
          businessName="metrocalma - Centro de Bem-Estar Mental Premium"
          address={{
            addressLocality: "São Paulo",
            addressRegion: "SP",
            addressCountry: "BR"
          }}
          geo={{
            latitude: -23.5505,
            longitude: -46.6333
          }}
          telephone="+551199999999"
          description="Centro premium especializado em bem-estar mental e emocional, oferecendo jornadas personalizadas de autoconhecimento com tecnologia avançada."
          url="https://metrocalma.org"
          priceRange="$$"
          openingHours={[
            "Mo-Fr 09:00-18:00",
            "Sa 10:00-14:00"
          ]}
          sameAs={[
            "https://facebook.com/metrocalma",
            "https://instagram.com/metrocalma_sp"
          ]}
        />
      )}
      
      {/* Above the fold content (loaded immediately) */}
      <NavbarSection />
      
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={shouldSimplifyAnimations ? fadeInVariants : fadeInVariants}
      >
        <HeroSection />
      </motion.div>
      
      <IntroCards />
      
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={shouldSimplifyAnimations ? fadeInVariants : slideInVariants.up}
        transition={{ delay: shouldSimplifyAnimations ? 0 : 0.2 }}
      >
        <DemoSection />
      </motion.div>

      {/* Premium sections with enhanced styling */}
      <BenefitsSection />
      
      {/* NEW: 3 Passos da Transformação Section */}
      {initialContentLoaded && (
        <OptimizedSectionLoader
          Component={LazyTransformationStepsSection}
          placeholderBg="bg-gradient-to-br from-white via-blue-50/30 to-purple-50/20" 
          placeholderHeight="min-h-[80vh]" 
          animation="fade"
          priority="high"
        />
      )}
      
      {/* TrustSection removida do build */}

      {/* Optimized lazy loaded sections */}
      {initialContentLoaded && (
        <>
          <OptimizedSectionLoader
            Component={LazyCalmBreathingSection}
            placeholderBg="bg-gradient-to-br from-brand-primary/20 via-transparent to-transparent" 
            placeholderHeight="min-h-screen" 
            animation="fade"
            priority="medium"
            style={{ 
              background: `linear-gradient(135deg, rgba(97, 46, 215, 0.2) 0%, rgba(153, 183, 175, 0.1) 50%, transparent 100%)` 
            }}
          />

          <OptimizedSectionLoader
            Component={LazyMindBodyConnectionSection}
            placeholderBg="bg-gradient-to-br from-brand-secondary/20 via-transparent to-brand-primary/5 backdrop-blur-sm" 
            placeholderHeight="min-h-screen" 
            animation="fade"
            priority="low"
            style={{ 
              background: `linear-gradient(135deg, rgba(132, 102, 218, 0.2) 0%, rgba(153, 183, 175, 0.1) 30%, rgba(97, 46, 215, 0.05) 100%)` 
            }}
          />
          
          <OptimizedSectionLoader
            Component={LazyPremiumDemo}
            placeholderBg="bg-gradient-to-br from-white/98 to-brand-accent/5 backdrop-blur-sm" 
            placeholderHeight="min-h-[50vh]"
            priority="low"
          />
          
          <OptimizedSectionLoader
            Component={LazyEmotionalTrackingSection}
            placeholderBg="bg-gradient-to-br from-brand-accent/3 to-white/99 backdrop-blur-sm" 
            placeholderHeight="min-h-[40vh]"
            priority="low"
          />
          
          <OptimizedSectionLoader
            Component={LazyWorkPerformanceSection}
            placeholderBg="bg-gradient-to-br from-white/98 to-brand-accent/5 backdrop-blur-sm" 
            placeholderHeight="min-h-[40vh]"
            priority="low"
          />
          
          <OptimizedSectionLoader
            Component={LazyFeaturesSection}
            placeholderBg="bg-gradient-to-br from-brand-accent/3 to-white/99 backdrop-blur-sm" 
            placeholderHeight="min-h-[40vh]"
            priority="low"
          />
          
          <OptimizedSectionLoader
            Component={LazyAdaptiveSection}
            placeholderBg="bg-gradient-to-br from-brand-accent/5 to-white/98 backdrop-blur-sm" 
            placeholderHeight="min-h-[60vh]"
            priority="low"
          />
          
          <OptimizedSectionLoader
            Component={LazyTestimonialsSection}
            placeholderBg="bg-gradient-to-br from-white/99 to-brand-accent/7 backdrop-blur-sm" 
            placeholderHeight="min-h-[40vh]"
            priority="low"
          />
          
          <OptimizedSectionLoader
            Component={LazyFaqSection}
            placeholderBg="bg-gradient-to-br from-brand-accent/3 to-white/99 backdrop-blur-sm" 
            placeholderHeight="min-h-[40vh]"
            priority="low"
          />
          
          <OptimizedSectionLoader
            Component={LazyCtaSection}
            placeholderBg="bg-gradient-to-br from-brand-primary/15 to-brand-secondary/10 backdrop-blur-sm" 
            placeholderHeight="min-h-[30vh]"
            animation="fade"
            priority="low"
          />
          
          <OptimizedSectionLoader
            Component={LazyFooterSection}
            placeholderBg="bg-gradient-to-br from-white/98 to-brand-accent/5 backdrop-blur-sm" 
            placeholderHeight="min-h-[20vh]"
            animation="fade"
            priority="low"
          />
        </>
      )}
    </motion.div>
  );
};

export default Index;
